import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { getIcon } from "../lib/icons";
import { mediaUrl, publicApi } from "../lib/api";
import { videoEmbedUrl } from "../lib/utils";
import { RichContent } from "../lib/richText";

const DEFAULT_BTN = "#2EA3FD";

// Section backgrounds drawn from the site palette so pages match the rest of the website.
const BG_CLASS = { white: "bg-white", light: "bg-slate-50", navy: "bg-navy" };
const sectionBg = (block, fallback) => BG_CLASS[block.bg || fallback] || BG_CLASS[fallback];
const isDark = (block, fallback) => (block.bg || fallback) === "navy";

const CTAButton = ({ text, link, color }) => {
  if (!text) return null;
  const isInternal = link && link.startsWith("/");
  const style = { backgroundColor: color || DEFAULT_BTN };
  const cls = "inline-flex items-center gap-2 text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5 hover:brightness-110";
  if (isInternal) return <Link to={link} style={style} className={cls}>{text} <ArrowRight size={18} /></Link>;
  return <a href={link || "#"} style={style} className={cls} target={link ? "_blank" : undefined} rel="noopener noreferrer">{text} <ArrowRight size={18} /></a>;
};

// Shared by the Video block and the media column of Image + Text.
const VideoPlayer = ({ block, testid }) => {
  const embed = block.video ? "" : videoEmbedUrl(block.videoUrl);
  if (!block.video && !embed) return null;
  const poster = block.poster ? mediaUrl(block.poster) : (block.video ? mediaUrl(`${block.video}/poster`) : "");
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg bg-black aspect-video">
      {block.video ? (
        <video
          data-testid={testid || "page-video-player"}
          src={mediaUrl(block.video)}
          poster={poster || undefined}
          className="absolute inset-0 w-full h-full object-cover"
          controls
          playsInline
          preload="metadata"
        />
      ) : (
        <iframe
          data-testid="page-video-embed"
          src={embed}
          title={block.heading || block.caption || "Video"}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      )}
    </div>
  );
};

const hasVideo = (block) => !!(block.video || videoEmbedUrl(block.videoUrl));

const FORM_FIELDS = [
  { key: "name", label: "Your name", type: "text", autoComplete: "name" },
  { key: "email", label: "Email address", type: "email", autoComplete: "email" },
  { key: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
  { key: "business", label: "Business name", type: "text", autoComplete: "organization" },
  { key: "message", label: "Message", type: "textarea" },
];

function FormBlock({ block, pageTitle }) {
  const enabled = block.fields || { name: true, email: true, phone: true, message: true };
  const active = FORM_FIELDS.filter((f) => enabled[f.key]);
  const [values, setValues] = useState({});
  const [website, setWebsite] = useState(""); // honeypot
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const color = block.buttonColor || DEFAULT_BTN;

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!values.name || !values.email) { setError("Please add your name and email."); return; }
    setSending(true);
    try {
      await publicApi.submitPageForm({ ...values, website, page_title: pageTitle || block.heading || "Page form" });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <section className={`py-16 md:py-20 ${sectionBg(block, "light")}`} data-testid="page-block-form">
      <div className="max-w-2xl mx-auto px-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 md:p-9">
          {block.heading && <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-2">{block.heading}</h2>}
          {block.intro && <p className="text-slate-500 mb-6 leading-relaxed">{block.intro}</p>}
          {done ? (
            <div className="text-center py-8" data-testid="page-form-success">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4"><CheckCircle2 size={30} /></span>
              <p className="font-display font-bold text-xl text-navy">{block.successMessage || "Thank you — we'll be in touch shortly."}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="page-form">
              <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              {active.map((f) => (
                <div key={f.key}>
                  <label className="block text-sm font-semibold text-navy mb-1.5">{f.label}{(f.key === "name" || f.key === "email") && <span className="text-orange"> *</span>}</label>
                  {f.type === "textarea" ? (
                    <textarea data-testid={`page-form-${f.key}`} rows={4} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" />
                  ) : (
                    <input data-testid={`page-form-${f.key}`} type={f.type} autoComplete={f.autoComplete} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" />
                  )}
                </div>
              ))}
              {error && <p className="text-sm text-red-500" data-testid="page-form-error">{error}</p>}
              <button type="submit" disabled={sending} data-testid="page-form-submit" style={{ backgroundColor: color }} className="inline-flex items-center gap-2 text-white font-bold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-60">
                {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : (block.buttonText || "Send")}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Block({ block, pageTitle }) {
  const t = block.type;

  if (t === "hero") {
    return (
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          {block.eyebrow && <span className="inline-block text-orange font-bold text-sm tracking-widest uppercase mb-4">{block.eyebrow}</span>}
          {block.heading && <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] max-w-4xl">{block.heading}</h1>}
          {block.subheading && <p className="mt-5 text-lg text-slate-300 max-w-2xl leading-relaxed">{block.subheading}</p>}
          {block.buttonText && <div className="mt-8"><CTAButton text={block.buttonText} link={block.buttonLink} color={block.buttonColor || "#F26A21"} /></div>}
        </div>
      </section>
    );
  }

  if (t === "text") {
    const dark = isDark(block, "white");
    return (
      <section className={`py-16 md:py-20 ${sectionBg(block, "white")}`}>
        <div className="max-w-3xl mx-auto px-6">
          {block.heading && <h2 className={`font-display font-extrabold text-3xl md:text-4xl mb-6 ${dark ? "text-white" : "text-navy"}`}>{block.heading}</h2>}
          <div className={dark ? "on-dark" : ""}><RichContent text={block.body} /></div>
        </div>
      </section>
    );
  }

  if (t === "imageText") {
    const right = block.imagePosition === "right";
    const dark = isDark(block, "white");
    return (
      <section className={`py-16 md:py-20 ${sectionBg(block, "white")}`}>
        <div className={`max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center`}>
          <div className={right ? "md:order-2" : ""} data-testid={hasVideo(block) ? "page-block-imagetext-video" : undefined}>
            {hasVideo(block) ? (
              <VideoPlayer block={block} testid="page-imagetext-video-player" />
            ) : block.image ? (
              <img loading="lazy" decoding="async" src={mediaUrl(block.image)} alt={block.imageAlt || block.heading || ""} className="rounded-2xl shadow-lg w-full object-cover" />
            ) : (
              <div className="rounded-2xl bg-slate-100 aspect-video" />
            )}
          </div>
          <div className={right ? "md:order-1" : ""}>
            {block.heading && <h2 className={`font-display font-extrabold text-3xl md:text-4xl mb-5 ${dark ? "text-white" : "text-navy"}`}>{block.heading}</h2>}
            <div className={dark ? "on-dark" : ""}><RichContent text={block.body} /></div>
            {block.buttonText && <div className="mt-6"><CTAButton text={block.buttonText} link={block.buttonLink} color={block.buttonColor} /></div>}
          </div>
        </div>
      </section>
    );
  }

  if (t === "image") {
    if (!block.image) return null;
    const narrow = block.width === "narrow";
    return (
      <section className={`py-12 md:py-16 ${sectionBg(block, "white")}`} data-testid="page-block-image">
        <div className={`${narrow ? "max-w-3xl" : "max-w-6xl"} mx-auto px-6`}>
          <figure>
            <img loading="lazy" decoding="async" src={mediaUrl(block.image)} alt={block.caption || ""} className="rounded-2xl shadow-lg w-full object-cover" />
            {block.caption && <figcaption className={`text-center text-sm mt-3 ${isDark(block, "white") ? "text-slate-300" : "text-slate-400"}`}>{block.caption}</figcaption>}
          </figure>
        </div>
      </section>
    );
  }

  if (t === "video") {
    if (!hasVideo(block)) return null;
    const narrow = block.width === "narrow";
    const dark = isDark(block, "white");
    return (
      <section className={`py-12 md:py-16 ${sectionBg(block, "white")}`} data-testid="page-block-video">
        <div className={`${narrow ? "max-w-3xl" : "max-w-5xl"} mx-auto px-6`}>
          {block.heading && <h2 className={`font-display font-extrabold text-3xl md:text-4xl mb-6 text-center ${dark ? "text-white" : "text-navy"}`}>{block.heading}</h2>}
          <figure>
            <VideoPlayer block={block} />
            {block.caption && <figcaption className={`text-center text-sm mt-3 ${dark ? "text-slate-300" : "text-slate-400"}`}>{block.caption}</figcaption>}
          </figure>
        </div>
      </section>
    );
  }

  if (t === "cards") {
    const cards = block.cards || [];
    const dark = isDark(block, "light");
    return (
      <section className={`py-16 md:py-20 ${sectionBg(block, "light")}`}>
        <div className="max-w-7xl mx-auto px-6">
          {block.heading && <h2 className={`font-display font-extrabold text-3xl md:text-4xl mb-10 text-center ${dark ? "text-white" : "text-navy"}`}>{block.heading}</h2>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((c, i) => {
              const Icon = getIcon(c.icon || "CheckCircle2");
              return (
                <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-orange/10 flex items-center justify-center text-orange mb-4"><Icon size={22} /></div>
                  <h3 className="font-display font-bold text-lg text-navy mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (t === "cta") {
    const dark = isDark(block, "navy");
    return (
      <section className={`py-16 ${sectionBg(block, "navy")} ${dark ? "text-white" : ""}`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          {block.heading && <h2 className={`font-display font-extrabold text-3xl md:text-4xl mb-4 ${dark ? "text-white" : "text-navy"}`}>{block.heading}</h2>}
          {block.text && <p className={`mb-8 max-w-2xl mx-auto ${dark ? "text-slate-300" : "text-slate-500"}`}>{block.text}</p>}
          <CTAButton text={block.buttonText} link={block.buttonLink} color={block.buttonColor || "#F26A21"} />
        </div>
      </section>
    );
  }

  if (t === "form") {
    return <FormBlock block={block} pageTitle={pageTitle} />;
  }

  return null;
}

export default function PageBlocks({ blocks = [], pageTitle }) {
  return (
    <>
      {blocks.map((b, i) => <Block key={i} block={b} pageTitle={pageTitle} />)}
    </>
  );
}
